/**
 * The phone path (variant A): every lesson of the book as four chapter nodes on a zigzag —
 * done = green check, current = big blue node with the robo-cat beside it, open (unlocked by
 * the placement test) = light blue, locked = grey.
 * Lessons that are not imported yet show one locked «скоро» node.
 */
import { memo, useEffect, useRef, type CSSProperties } from 'react';
import type { LessonMeta } from '../data/api.ts';
import { CHAPTERS, chapterStatuses, type ChapterStatus, type StepsState } from '../core/lesson-plan.ts';
import type { PathItem } from './LessonPath.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { RoboCat } from './RoboCat.tsx';
import './chapter-path.css';

const CHAPTER_ICON: Readonly<Record<(typeof CHAPTERS)[number]['id'], IconName>> = {
  words: 'cards',
  text: 'text',
  review: 'refresh',
  tasks: 'task',
};

/** Horizontal offsets (px) that make the column of nodes swing left and right. */
const SWING = [0, -64, -92, -64, 0, 64, 92, 64];

const STATUS_TEXT: Record<ChapterStatus, string> = { done: 'пройдена', current: 'сейчас', open: 'открыта', locked: 'впереди' };

/** Memoised: home re-renders on every XP tick, the path only when lessons or progress change. */
export const ChapterPath = memo(function ChapterPath({
  items,
  progress,
  onOpen,
}: {
  items: readonly PathItem[];
  progress: Readonly<Record<string, { steps: StepsState } | undefined>>;
  onOpen: (lessonId: string) => void;
}) {
  const currentRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  let node = 0;
  const swing = () => SWING[node++ % SWING.length]!;

  return (
    <ol className="cpath">
      {items.map(({ lesson, status }) => (
        <li key={lesson.id} className="cpath__lesson">
          <LessonHeader lesson={lesson} />
          {lesson.available ? (
            <ol className="cpath__nodes">
              {chapterStatuses(status, progress[lesson.id]?.steps).map((chapterStatus, i) => {
                const chapter = CHAPTERS[i]!;
                const offset = swing();
                const current = chapterStatus === 'current';
                return (
                  <li
                    key={chapter.id}
                    className={`cpath__node cpath__node--${chapterStatus}`}
                    style={{ '--x': `${offset}px` } as CSSProperties}
                  >
                    <button
                      ref={current ? currentRef : undefined}
                      type="button"
                      className="cpath__button"
                      disabled={chapterStatus === 'locked'}
                      onClick={() => onOpen(lesson.id)}
                      aria-label={`Урок ${lesson.number}, глава ${i + 1} «${chapter.name}» — ${STATUS_TEXT[chapterStatus]}`}
                    >
                      <Icon name={chapterStatus === 'done' ? 'check' : CHAPTER_ICON[chapter.id]} size={current ? 34 : 30} />
                    </button>
                    <span className="cpath__label" aria-hidden="true">
                      {chapter.name}
                    </span>
                    {current && (
                      <span className={`cpath__cat cpath__cat--${offset > 0 ? 'left' : 'right'}`} aria-hidden="true">
                        <RoboCat size={78} alive />
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="cpath__nodes">
              <div className="cpath__node cpath__node--soon" style={{ '--x': `${swing()}px` } as CSSProperties}>
                <span className="cpath__button" role="img" aria-label={`Урок ${lesson.number} — скоро`}>
                  <Icon name="lock" size={28} />
                </span>
                <span className="cpath__label" aria-hidden="true">
                  скоро
                </span>
              </div>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
});

function LessonHeader({ lesson }: { lesson: LessonMeta }) {
  return (
    <div className="cpath__header">
      <span className="cpath__number">Урок {lesson.number}</span>
      <span className="cpath__title han">{lesson.title}</span>
      {lesson.title_ru && <span className="cpath__title-ru">{lesson.title_ru}</span>}
    </div>
  );
}
